<template>
  <a
    class="card"
    :class="{ 'card--sizes-panel-open': sizesPanelIsOpen, 'is-selected': cardIsSelected }"
    :href="productUrl"
    :style="`background-color: ${backgroundColor}`"
    @click="handleClick($event)"
    @mouseover="selectCard(true)"
    @mouseleave="selectCard(false)"
  >
    <div class="card__image-container" ref="imageContainer">
      <Badge :badgeTypes="badgeTypes" />
      <transition name="bg-img-fade">
        <video
          v-if="shouldShowBackground && backgroundVideoUrl"
          class="card__background-video"
          ref="backgroundVideo"
          autoplay="true"
          loop="true"
        >
          <source :src="backgroundVideoUrl" type="video/mp4" />
        </video>
        <ImageContentful
          v-else-if="shouldShowBackground"
          :src="backgroundImageUrl"
          containerSelector=".card__image-container"
          :isBackgroundImage="true"
          :options="{
            imageCompression: 81,
            retinaSupportFactor: 0.5,
            fit: CONTENTFUL_IMAGE_FIT.fill,
          }"
          class="card__background-image"
          :alt="card.name"
        />
      </transition>
      <ImageContentful
        v-show="!shouldShowBackground"
        :src="currentVariant.imageUrl"
        :lazyload="{ rootMargin: '100%' }"
        containerSelector=".card__image"
        :options="{
          imageCompression: 71,
          format: imageFormat,
          retinaSupportFactor: 0.25,
          setHeight: false,
          backgroundColor: backgroundColor,
        }"
        class="card__image"
        :alt="card.name"
      />
    </div>
    <div class="card__content">
      <QuickAdd
        :variant="currentVariant"
        :onSizesPanelChange="onSizesPanelChange"
        :buttonText="$t('filter.quickadd')"
      />
      <div class="card__bottom">
        <div class="card__infos" :class="{ 'card__infos--has-background': shouldShowBackground }">
          <div class="card__title">{{ card.name }}</div>
          <div class="card__summary" v-if="!summaryIsHidden">{{ cardSummary }}</div>
          <div class="card__price">
            {{ priceWithCurrency }}
            <span class="card__separator"></span>
            {{ cardColorNames }}
          </div>
        </div>
        <ColorSelector
          :onColorSelect="onColorSelect"
          :variants="card.variants"
          :currentVariantId="currentVariant.id || 0"
          :onMouseevent="setColorSelectorHover"
          :isShowingGenderLabels="isShowingGenderLabels"
        />
      </div>
    </div>
  </a>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { onLoad } from '@/helpers/event'
import { format } from '@/helpers/currency-formatter'
import Badge from './Badge/Badge.vue'
import ColorSelector from './ColorSelector/ColorSelector.vue'
import QuickAdd from './QuickAdd/QuickAdd.vue'
import ImageContentful, {
  CONTENTFUL_IMAGE_FIT,
  CONTENTFUL_IMAGE_FORMAT,
} from '@/components/_shared/ImageContentful.vue'

const BADGES = [
  { type: 'new', condition: 'isNew' },
  { type: 'lowStock', condition: 'isLowOnStock' },
]

export default {
  name: 'Card',
  components: { Badge, ColorSelector, ImageContentful, QuickAdd },

  props: {
    card: {
      type: Object,
      required: true,
    },

    backgroundColor: {
      type: String,
      default: '#f7f7f7',
    },

    imageFormat: {
      type: String,
      default: CONTENTFUL_IMAGE_FORMAT.default,
      validator(value) {
        return Object.values(CONTENTFUL_IMAGE_FORMAT).includes(value)
      },
    },

    isShowingGenderLabels: {
      type: Boolean,
      default: false,
    },

    summaryIsHidden: {
      type: Boolean,
      required: false,
      default: false,
    },

    onCardClick: {
      type: Function,
      required: false,
      default() {
        return () => {}
      },
    },
  },

  data() {
    return {
      currentVariantId: this.card.displayedVariantId,
      sizesPanelIsOpen: false,
      cardIsSelected: false,
      isHover: false,
      colorSelectorIsHovered: false,
      CONTENTFUL_IMAGE_FIT,
      imageContainerWidth: 0,
    }
  },

  computed: {
    ...mapState('store', ['currency']),
    ...mapGetters({
      vpWidth: 'viewport/width',
      isMqLarge: 'viewport/isMqLarge',
    }),

    badgeTypes() {
      return BADGES.reduce((types, badge) => {
        if (this[badge.condition]) types.push(badge.type)
        return types
      }, [])
    },

    isLowOnStock() {
      // Definition of "low stock" according to Looker "Product Availability" dashboard:
      // => less than half of the size curve available
      const soldOutSkus = this.currentVariant.skus.reduce((soldOutSkus, sku) => {
        if (sku.stock === 0) soldOutSkus.push(sku)
        return soldOutSkus
      }, [])
      return soldOutSkus.length > this.currentVariant.skus.length / 2
    },

    isMobile() {
      return !this.isMqLarge
    },

    isNew() {
      return this.currentVariant.filterValues?.release_date?.includes('new-arrivals')
    },

    backgroundImageUrl() {
      return this.currentVariant.backgroundImageUrl
    },

    backgroundVideoUrl() {
      return this.currentVariant.backgroundVideoUrl
    },

    shouldShowBackground() {
      return (
        this.cardIsSelected &&
        !this.colorSelectorIsHovered &&
        !this.isMobile &&
        (this.backgroundImageUrl || this.backgroundVideoUrl)
      )
    },

    currentVariant() {
      const foundVariant = this.card.variants.find(variant => variant.id === this.currentVariantId)
      const maybeFirstVariant = this.card.variants[0]
      return foundVariant || maybeFirstVariant || {}
    },

    productUrl() {
      let url = ''
      if (this.card.variants.length) {
        url = this.currentVariant.productUrl
        if (window.document.body.classList.contains('iframe-layout')) {
          const separator = url.indexOf('?') > -1 ? '&' : '?'
          url = url + separator + 'iframe=true'
        }
      }
      return url
    },

    // TODO: Remove once A/B test is over #DY-FILTER-PAGE-SHORT-PRODUCT-DESCRIPTION
    cardSummary() {
      const filterStore = this.$store.state.filter
      let summary = this.card.summary

      if (filterStore && filterStore.preferredCardSummary[this.card.name]) {
        const preferredSummary = filterStore.preferredCardSummary[this.card.name]

        /**
         * preferredSummary is usually a string but it can happen that two filter page item have
         * the same name. In this case, it is possible to be more specific by having a
         * default value + a value for a specific product group slug
         * @example
         *   'Weather Shirt': {
         *     default: 'Our original long-sleeve for cold-weather training.',
         *     'weather-shirt-2019': 'The next-gen training top for cold conditions.',
         *   },
         */
        summary =
          typeof preferredSummary !== 'string'
            ? preferredSummary[this.card.productGroup] || preferredSummary.default
            : preferredSummary
      }
      return summary
    },

    cardColorNames() {
      return this.card.variants.length ? this.currentVariant.colorName : []
    },

    priceWithCurrency() {
      const amount = Number(this.card.price)
      if (this.card.price !== undefined) return format(amount, this.currency)
    },
  },

  watch: {
    vpWidth: 'updateImageContainerWidth',
  },

  mounted() {
    this.updateImageContainerWidth()
    onLoad().then(this.fetchStoreData)
  },

  methods: {
    fetchStoreData() {
      this.$store.dispatch('store/fetchData')
    },

    onColorSelect(id) {
      this.currentVariantId = id
    },

    onSizesPanelChange(status) {
      this.sizesPanelIsOpen = status
    },

    handleClick(event) {
      if (this.sizesPanelIsOpen) event.preventDefault()
      this.onCardClick(this.currentVariant.spreeProduct, event)
    },

    selectCard(selected) {
      this.cardIsSelected = selected
      this.$store.commit('nav/setActiveCardVariant', this.currentVariant)
    },

    setColorSelectorHover(isHover) {
      this.colorSelectorIsHovered = isHover
    },

    updateImageContainerWidth() {
      this.imageContainerWidth = this.$refs.imageContainer
        ? this.$refs.imageContainer.offsetWidth
        : 0
    },
  },
}
</script>

<style lang="scss">
@import './card';
</style>
